import { useMemo } from "react"
import { formatDateString } from "@/v2-common/utils/utils.date"
import cn from "@/v2-ui/utils/utils.cn"
import Separator from "@/v2-ui/separator"
import { errorToast } from "@/v2-ui/toast"
import { DotText } from "@/v2-backoffice-client/components/dot"
import Link from "@/v2-backoffice-client/components/link"
import Table, {
  createColumns,
  type TableProps,
} from "@/v2-backoffice-client/components/table"
import { HUBSPOT_BASE_URL } from "@/v2-backoffice-client/constants"
import { buildCustomerViewLink } from "@/v2-backoffice-client/customer/customer.links"
import CustomerMergeResolveCell from "@/v2-backoffice-client/customer/merge/CustomerMergeResolveCell"
import { useCustomerMergesQuery } from "@/v2-backoffice-client/customer/merge/customer.merge.query"
import {
  CUSTOMER_MERGE_OPEN_STATUSES,
  CUSTOMER_MERGE_STATUSES,
  describeHoldings,
  describeMerge,
  formatMergeSide,
  getCustomerMergeStatusDisplay,
} from "@/v2-backoffice-client/customer/merge/customer.merge.utils"
import type {
  CustomerMergeFragment,
  CustomerMergeSideFragment,
} from "@/v2-backoffice-client/customer/merge/__types__/customer.merge.fragment"

const COLUMN_SIZING = {
  Status: 150,
  Leftover: 220,
  Survivor: 220,
  What: 360,
  Decide: 420,
  ObservedAt: 110,
}

const DECIDE_DESCRIPTION =
  "The two rows a HubSpot merge left for one company become one: the keeper keeps, the other row's teams and users move onto it (on v1 and v2, licenses and seats follow), and the surviving company's link follows when the row merged away keeps. The filled button is what the Backoffice does on its own: the row with the licence or billing account keeps; with none on either side, the row HubSpot kept. Keep separate leaves both as they are - or, on a consolidated merge, moves everything back."

type CreateColumnsInput = {
  onChanged(): void
}

/** The leftover or survivor as a link into its customer panel, or the bare company id. */
function renderSide(
  side: CustomerMergeSideFragment | null | undefined,
  externalCompanyId: string,
  holdings: Parameters<typeof describeHoldings>[0],
) {
  const name = side ? formatMergeSide(side) : null
  return (
    <span className="inline-flex min-w-0 flex-col leading-tight">
      {side?.ExternalCustomerId ? (
        <Link
          to={buildCustomerViewLink(side.ExternalCustomerId)}
          className="truncate font-medium"
        >
          {name}
        </Link>
      ) : (
        <span className="truncate text-muted-foreground">
          {name ?? "No customer"}
        </span>
      )}
      <span className="truncate text-xs text-muted-foreground">
        {side && holdings ? `${describeHoldings(holdings)} · ` : ""}
        <Link
          external
          to={`${HUBSPOT_BASE_URL}/${externalCompanyId}`}
          className="hover:underline"
        >
          {externalCompanyId}
        </Link>
      </span>
    </span>
  )
}

function createCustomerMergeColumns(input: CreateColumnsInput) {
  const { onChanged } = input
  return createColumns<CustomerMergeFragment>({
    enableSelection: false,
    enableSorting: true,
    enableHiding: true,
    columns: {
      Status: {
        id: "Status",
        label: "Status",
        description:
          "Where the merge stands on our side. Decisions first, then what the Backoffice is about to do, then what the sync still owes, then what is done.",
        enableGrouping: true,
        filter: {
          type: "select",
          options: CUSTOMER_MERGE_STATUSES.map((status) => ({
            value: getCustomerMergeStatusDisplay(status).label,
            label: getCustomerMergeStatusDisplay(status).label,
            dotVariant: getCustomerMergeStatusDisplay(status).variant,
          })),
        },
        getValue: (row) => getCustomerMergeStatusDisplay(row.Status).label,
        renderCell({ row }) {
          const display = getCustomerMergeStatusDisplay(row.original.Status)
          return (
            <DotText variant={display.variant} title={display.description}>
              {display.label}
            </DotText>
          )
        },
      },
      Leftover: {
        id: "Leftover",
        label: "Merged away",
        description:
          "The HubSpot company that no longer exists and the customer row that carried it: what the merge left behind, with what it still holds.",
        filter: { type: "text", placeholder: "Customer or company id..." },
        getValue: (row) =>
          `${formatMergeSide(row.MergedCustomer)} ${row.ExternalCompanyId}`,
        renderCell({ row }) {
          // The row carrying the merged-away id; its holdings are listed
          // only while it is the leftover (otherwise it is the survivor's
          // holder, described in the next column).
          return renderSide(
            row.original.MergedCustomer,
            row.original.ExternalCompanyId,
            row.original.LeftoverCustomer
              ? row.original.LeftoverHoldings
              : null,
          )
        },
      },
      Survivor: {
        id: "Survivor",
        label: "Merged into",
        description:
          "The HubSpot company that survived and the customer row linked to it, with what it holds.",
        filter: { type: "text", placeholder: "Customer or company id..." },
        getValue: (row) =>
          `${formatMergeSide(row.SurvivingCustomer)} ${row.SurvivingExternalCompanyId}`,
        renderCell({ row }) {
          return renderSide(
            row.original.SurvivingCustomer,
            row.original.SurvivingExternalCompanyId,
            row.original.SurvivorHoldings,
          )
        },
      },
      What: {
        id: "What",
        label: "What happens",
        description:
          "What the Backoffice did, is about to do, or is waiting for - and why.",
        enableSorting: false,
        getValue: (row) => describeMerge(row),
        renderCell({ row }) {
          return (
            <span className="whitespace-normal text-xs leading-snug text-muted-foreground">
              {describeMerge(row.original)}
            </span>
          )
        },
      },
      Decide: {
        id: "Decide",
        label: "Decide",
        description: DECIDE_DESCRIPTION,
        enableSorting: false,
        isWrapped: true,
        getValue: (row) => row.KeeperCustomer?.Name ?? "",
        renderCell({ row }) {
          return (
            <CustomerMergeResolveCell
              merge={row.original}
              onResolved={onChanged}
            />
          )
        },
      },
      ObservedAt: {
        id: "ObservedAt",
        label: "Observed",
        description:
          "When the sync last asked HubSpot about the merged company.",
        getValue: (row) => row.ObservedAt,
        renderCell({ row }) {
          return formatDateString(row.original.ObservedAt)
        },
        exportValue({ row }) {
          return formatDateString(row.original.ObservedAt)
        },
      },
    },
  })
}

export type CustomerMergeTableProps = Omit<
  TableProps<CustomerMergeFragment>,
  "data" | "columns"
>

/**
 * The CRM Merges worklist: every HubSpot company merge the CRM sync has
 * recorded, with the row it left behind, the row holding the survivor,
 * what each holds, what the Backoffice did or will do about it, and the
 * operator's choices. Owns the query, shared through the Apollo cache with
 * the tab trigger's count. Every action is a row's own (the Decide cell);
 * the automatic pass needs no button, it runs by itself while staff use
 * the Backoffice.
 */
function CustomerMergeTable(props: CustomerMergeTableProps) {
  const { className, ...tableProps } = props

  const { data, loading, error, refetch } = useCustomerMergesQuery()
  const rows = data?.getCustomerMerges ?? []

  const openCount = useMemo(
    () =>
      rows.filter((row) => CUSTOMER_MERGE_OPEN_STATUSES.includes(row.Status))
        .length,
    [rows],
  )

  const columns = useMemo(
    () =>
      createCustomerMergeColumns({
        onChanged() {
          void refetch()
        },
      }),
    [refetch],
  )

  return (
    <Table
      className={cn("customer-merge-table", className)}
      tableId="customer-merge-table"
      data={rows}
      columns={columns}
      initialColumnSizing={COLUMN_SIZING}
      enableGrouping
      {...tableProps}
    >
      <Table.Toolbar>
        <span className="text-sm text-muted-foreground">
          {loading
            ? "Loading..."
            : `${openCount} of ${rows.length} merge${rows.length === 1 ? "" : "s"} still open`}
        </span>
        <div className="flex-1" />
        <Table.Search placeholder="Search by customer or company id..." />

        <Table.ToolbarFilters />
        <Separator orientation="vertical" />

        <Table.ToolbarMenu
          onRefresh={() => {
            void refetch()
          }}
          exportFileName="customer-merges"
        />
      </Table.Toolbar>

      <Table.FilterList />
      <Table.GroupList />

      <Table.Content countLabel="merges" isLoading={loading} error={error} />
    </Table>
  )
}

export default CustomerMergeTable
